import React from 'react';
import {
  UserGroupIcon,
  CheckCircleIcon,
  PlayIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  CogIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export default function OnboardingHomeHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-4 md:mb-6">
            <SparklesIcon className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Onboarding Guide</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Your step-by-step journey to set up and customize your workspace
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-start space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-12 h-12 md:w-12 md:h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is the Onboarding Process?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                Think of it as your personal setup wizard. It's a guided checklist that walks you through every essential step to configure your account, add your team, and customize the system for your business. No more wondering where to start - just follow the steps and you'll have everything ready in no time.
              </p>
            </div>
          </div>
        </div>

        {/* Onboarding Layout Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 text-center">How Onboarding Works</h2>

          {/* Mobile Layout */}
          <div>
            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <DevicePhoneMobileIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 text-purple-600" />
              Mobile Experience
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 md:p-6 border-2 border-dashed border-gray-300 max-w-sm md:max-w-xs mx-auto">
              <div className="space-y-3 md:space-y-4">
                {/* Progress Bar */}
                <div className="bg-indigo-100 rounded p-3 border border-indigo-200">
                  <div className="w-full bg-indigo-200 rounded-full h-2 mb-2">
                    <div className="bg-indigo-500 h-2 rounded-full" style={{width: '43%'}}></div>
                  </div>
                  <p className="text-xs text-indigo-700 text-center font-medium">Step 3 of 7</p>
                </div>

                {/* Current Step */}
                <div className="bg-white rounded p-3 md:p-3 border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-1 text-sm md:text-base">Role Calibration</h4>
                  <p className="text-xs text-gray-600 mb-2">Set up team roles</p>
                  <div className="w-full h-2 bg-indigo-200 rounded mb-2"></div>
                  <button className="w-full py-2 bg-indigo-600 text-white rounded text-sm md:text-sm">
                    Continue
                  </button>
                </div>

                {/* Step List */}
                <div className="space-y-1">
                  <div className="flex items-center space-x-2 p-2 bg-green-50 rounded">
                    <CheckCircleIcon className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
                    <span className="text-xs text-green-800">Company Info</span>
                  </div>
                  <div className="flex items-center space-x-2 p-2 bg-indigo-50 rounded border border-indigo-300">
                    <span className="w-3 h-3 md:w-4 md:h-4 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs">3</span>
                    <span className="text-xs text-indigo-800">Role Calibration</span>
                  </div>
                  <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                    <span className="w-3 h-3 md:w-4 md:h-4 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xs">4</span>
                    <span className="text-xs text-gray-600">Policies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Onboarding Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <BuildingOfficeIcon className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Company Information</h3>
            <p className="text-gray-600 text-sm md:text-base">Enter your business details and basic setup</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <UsersIcon className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Role Calibration</h3>
            <p className="text-gray-600 text-sm md:text-base">Define roles and expectations for your team</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <ClipboardDocumentListIcon className="w-5 h-5 md:w-6 md:h-6 text-yellow-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Company Policies</h3>
            <p className="text-gray-600 text-sm md:text-base">Set up rules, guidelines, and procedures</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <UserGroupIcon className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Add Users</h3>
            <p className="text-gray-600 text-sm md:text-base">Invite team members and assign roles</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <CogIcon className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Departments & Teams</h3>
            <p className="text-gray-600 text-sm md:text-base">Organize your company structure</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <CurrencyDollarIcon className="w-5 h-5 md:w-6 md:h-6 text-red-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Staff Costs</h3>
            <p className="text-gray-600 text-sm md:text-base">Manage budgets and cost tracking</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow sm:col-span-2 lg:col-span-1">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <DocumentTextIcon className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Processes & SOPs</h3>
            <p className="text-gray-600 text-sm md:text-base">Create standard operating procedures</p>
          </div>
        </div>

        {/* Video Tutorials */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <PlayIcon className="w-5 h-5 md:w-6 md:h-6 mr-3 text-red-500" />
            Video Tutorials
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <PlayIcon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm md:text-base">Company Setup</h3>
                  <p className="text-sm text-gray-600">2:34 min</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm">Learn how to enter your business information and basic settings</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <PlayIcon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm md:text-base">Team Roles</h3>
                  <p className="text-sm text-gray-600">3:12 min</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm">Understand how to define roles and set expectations</p>
            </div>
          </div>

          <div className="mt-4 md:mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3">
              <EyeIcon className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-blue-800 font-medium text-sm md:text-base">Watch Anytime</p>
                <p className="text-blue-700 text-sm">Tutorial videos are embedded directly in each step for easy access</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 md:p-8 text-white mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Why Onboarding Matters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 md:w-6 md:h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1 text-sm md:text-base">Quick Setup</h3>
                <p className="text-indigo-100 text-sm md:text-base">Follow clear steps to get everything configured properly</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 md:w-6 md:h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1 text-sm md:text-base">Learn as You Go</h3>
                <p className="text-indigo-100 text-sm md:text-base">Videos and guides explain each step along the way</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 md:w-6 md:h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1 text-sm md:text-base">Track Progress</h3>
                <p className="text-indigo-100 text-sm md:text-base">Visual progress bar keeps you motivated and informed</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 md:w-6 md:h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1 text-sm md:text-base">Custom Fit</h3>
                <p className="text-indigo-100 text-sm md:text-base">Personalize the system to match your business needs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Getting Started */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <SparklesIcon className="w-5 h-5 md:w-6 md:h-6 mr-3 text-indigo-500" />
            Getting Started
          </h2>
          <div className="space-y-4 md:space-y-4">
            <div className="flex items-start space-x-3 md:space-x-4">
              <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Access Onboarding</h3>
                <p className="text-gray-600 text-sm md:text-base">Log in and you'll be automatically directed to onboarding if needed</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 md:space-x-4">
              <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Follow the Steps</h3>
                <p className="text-gray-600 text-sm md:text-base">Use the sidebar to see all steps and click to jump between them</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 md:space-x-4">
              <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Watch Tutorials</h3>
                <p className="text-gray-600 text-sm md:text-base">Click the play button next to any step for video guidance</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 md:space-x-4">
              <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Complete & Continue</h3>
                <p className="text-gray-600 text-sm md:text-base">Fill out forms and click "Continue" to move to the next step</p>
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-3">
                <ArrowPathIcon className="w-5 h-5 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-800 text-sm md:text-base">Auto-Save</h3>
                  <p className="text-green-700 text-sm">Your progress is saved automatically - come back anytime</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-3">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-blue-800 text-sm md:text-base">AI Assistant</h3>
                  <p className="text-blue-700 text-sm">Get instant help from the chat button anytime</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}